import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../service/api";

// ------------------------------
// Get All Maids
// ------------------------------
export const getAllMaids = createAsyncThunk(
  "maids/getAllMaids",
  async ({ hotelId, type }, { rejectWithValue }) => {
    try {
      const payload = { hotelId };
      if (type) payload.type = type;

      const { data } = await api.post("api/maids/get", payload);
      return { data, type };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || error.message || "Failed to fetch maids"
      );
    }
  }
);

// ------------------------------
// Create Maid
// ------------------------------
export const createMaid = createAsyncThunk(
  "maids/createMaid",
  async (maidData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("api/maids", maidData);
      return data.maid;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Failed to create maid"
      );
    }
  }
);

// ------------------------------
// Update Maid
// ------------------------------
export const updateMaid = createAsyncThunk(
  "maids/updateMaid",
  async (maidData, { rejectWithValue }) => {
    try {
      const { data } = await api.put("api/maids/update", maidData);
      return data.maid;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Failed to update maid"
      );
    }
  }
);

// ------------------------------
// Delete Maid
// ------------------------------
export const deleteMaid = createAsyncThunk(
  "maids/deleteMaid",
  async ({ maidId, maidType }, { rejectWithValue }) => {
    try {
      await api.delete(`api/maids/${maidId}`);
      return { _id: maidId, type: maidType };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Failed to delete maid"
      );
    }
  }
);

const initialState = {
  allMaids: [],
  maids: [],
  valets: [],
  bellmen: [],
  airbnbs: [],
  maid: null,

  fetchLoading: false,
  fetchError: null,

  createLoading: false,
  createError: null,
  createSuccess: false,

  updateLoading: false,
  updateError: null,
  updateSuccess: false,

  deleteLoading: false,
  deleteError: null,
  deleteSuccess: false,
};

const maidsSlice = createSlice({
  name: "maids",
  initialState,
  reducers: {
    clearMaidState: (state) => {
      state.createLoading = false;
      state.createError = null;
      state.createSuccess = false;
      state.updateLoading = false;
      state.updateError = null;
      state.updateSuccess = false;
      state.deleteLoading = false;
      state.deleteError = null;
      state.deleteSuccess = false;
      state.maid = null;
    },
  },
  extraReducers: (builder) => {
    // Get All Maids

    builder
      .addCase(getAllMaids.pending, (state) => {
        state.fetchLoading = true;
        state.fetchError = null;
      })
      .addCase(getAllMaids.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = null;

        const all = action.payload.data?.maids || action.payload.data || [];

        // Categorize staff by type
        state.allMaids = all;
        state.maids = all.filter((p) => p.type === "maid");
        state.valets = all.filter((p) => p.type === "valet");
        state.bellmen = all.filter((p) => p.type === "bellman");
        state.airbnbs = all.filter((p) => p.type === "airbnb");
      })
      .addCase(getAllMaids.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.payload;
      });

    // Create Maid

    builder
      .addCase(createMaid.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.createSuccess = false;
      })
      .addCase(createMaid.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.maid = action.payload;
        if (action.payload) {
          const newMaid = action.payload;
          state.allMaids = [...state.allMaids, newMaid];

          if (newMaid.type === "maid") state.maids = [...state.maids, newMaid];
          if (newMaid.type === "valet")
            state.valets = [...state.valets, newMaid];
          if (newMaid.type === "bellman")
            state.bellmen = [...state.bellmen, newMaid];
          if (newMaid.type === "airbnb")
            state.airbnbs = [...state.airbnbs, newMaid];
        }
      })
      .addCase(createMaid.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
        state.createSuccess = false;
      });

    // Update Maid

    builder
      .addCase(updateMaid.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateMaid.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        const updated = action.payload;

        if (updated?._id) {
          // Update all arrays where maid exists
          state.allMaids = state.allMaids.map((m) =>
            m._id === updated._id ? updated : m
          );
          state.maids = state.maids.map((m) =>
            m._id === updated._id ? updated : m
          );
          state.valets = state.valets.map((m) =>
            m._id === updated._id ? updated : m
          );
          state.bellmen = state.bellmen.map((m) =>
            m._id === updated._id ? updated : m
          );
          state.airbnbs = state.airbnbs.map((m) =>
            m._id === updated._id ? updated : m
          );
        }
      })
      .addCase(updateMaid.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
        state.updateSuccess = false;
      });

    // Delete Maid

    builder
      .addCase(deleteMaid.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteMaid.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        const { _id: deletedId, type: deletedType } = action.payload;

        // Remove from all arrays
        state.allMaids = state.allMaids.filter((m) => m._id !== deletedId);
        if (deletedType === "maid")
          state.maids = state.maids.filter((m) => m._id !== deletedId);
        if (deletedType === "valet")
          state.valets = state.valets.filter((m) => m._id !== deletedId);
        if (deletedType === "bellman")
          state.bellmen = state.bellmen.filter((m) => m._id !== deletedId);
        if (deletedType === "airbnb")
          state.airbnbs = state.airbnbs.filter((m) => m._id !== deletedId);
      })
      .addCase(deleteMaid.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
        state.deleteSuccess = false;
      });
  },
});

export const { clearMaidState } = maidsSlice.actions;
export default maidsSlice.reducer;
